import React, { useEffect, useMemo, useRef, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useSelector } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useGetAssetClasses from "@fartherfinance/frontend/api/Investment/hooks/useGetAssetClasses";
import useGetPortfolioAssetClasses from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioAssetClasses";
import useGetPortfolioPlanById from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanById";
import useGetPortfolioPlanFamilyPath from "@fartherfinance/frontend/api/Investment/hooks/useGetPortfolioPlanFamilyPath";
import useGetPositionsOrVirtualPortfolioPlanClassification from "@fartherfinance/frontend/api/Investment/hooks/useGetPositionsOrVirtualPortfolioPlanClassification";
import usePostPortfolioPlansBatch from "@fartherfinance/frontend/api/Investment/hooks/usePostPortfolioPlansBatch";
import {
  FinMasonError,
  PersonId,
} from "@fartherfinance/frontend/api/Investment/types";
import { AssetClassCategory } from "@fartherfinance/frontend/api/PerformanceGroups/requests/getHoldings";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import useGetTransitionalProposalPlanStats from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionalProposalPlanStats";
import {
  BasePermission,
  PortfolioPlanId,
  PortfolioTaxType,
  Ticker,
} from "@fartherfinance/frontend/api/Types";

import useIsAdmin from "../../../utils/useIsAdmin";
import CreateCustomOrPartnerModelModal from "../CreateCustomOrPartnerModelModal/CreateCustomOrPartnerModelModal";
import CreateCustomModelModal from "../CreateCustomPortfolioModal/CreateCustomModelModal";
import ModelBuilderTable from "../ModelBuilderTable/ModelBuilderTable";
import { TEMPORARY_SLEEVE_NAME } from "../ModelBuilderTable/TableRow/ParentRow";
import { InvestmentsPath } from "@src/config/routing/RouterPaths";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import Charts, {
  ChartLabel,
  getAllocations,
} from "@src/multiCustodian/components/Portfolio/shared/Charts";
import Details from "@src/multiCustodian/components/Portfolio/shared/Details";
import Stats from "@src/multiCustodian/components/Portfolio/shared/Stats";
import useCreateFamilyPathFromData from "@src/multiCustodian/components/Portfolio/shared/useCreateFamilyPathFromData";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import LogoWordmark from "@src/multiCustodian/theme/LogoWordmark";
import LinkButton from "@src/sharedComponents/LinkButton/LinkButton";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import PageHero from "@src/sharedComponents/PageHero/PageHero";
import ThreeDotsPopoverMenu from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/Menu";
import MenuItem from "@src/sharedComponents/Popover/ThreeDotsPopoverMenu/MenuItem";
import Text from "@src/sharedComponents/Typography/Text";
import { State } from "@src/store";

import { taxMapping, taxStatusIdToLabel, TEMPORARY_TICKER } from "./constants";
import constructFamilyPath from "./helpers/constructFamilyPath";
import genFinMasonErrorMsg from "./helpers/genFinMasonErrorMsg";
import getAssetClassCategoryLabelFromEnum from "./helpers/getAssetClassCategoryLabelFromEnum";
import getTaxTypeFromTags from "./helpers/getTaxTypeFromTags";
import { SET_MODEL_DETAILS, SET_TABLE } from "./reducer/actions";
import { usePortfolioBuilderContext } from "./reducer/Context";
import {
  addAssetClassToModel,
  addSecurityToAssetClassInModel,
  addSleeveToAssetClassInModel,
  areAnyWeightsZero,
  computeAssetClassWeightsHash,
  DEFAULT_MODEL_TABLE,
  generateElementId,
  generatePortfolioPlanId,
  getAssetClassAllocations,
  getFirstLevelSecuritiesAndSleeves,
  getTotalWeightOfNonAssetClasses,
  hasLooseSecuritiesOrSleeves,
  NO_ASSET_CLASS_ASSET_CLASS,
} from "./reducer/reducer";
import { AssetClass, ModelDetails, SecurityLookup } from "./reducer/Types";
import SearchBarAndTableConstructor from "./SearchBarAndTableConstructor/SearchBarAndTableConstructor";

import styles from "./PortfolioBuilder.module.css";

interface Props {
  portfolioPlanId: PortfolioPlanId | null;
}

const PortfolioBuilder: React.FC<Props> = ({ portfolioPlanId }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const [showPopoverMenu, setShowPopoverMenu] = useState<boolean>(false);
  const [showCustomModelModal, setShowCustomModelModal] =
    useState<boolean>(false);

  const [portfolioClassification, setPortfolioClassification] = useState<
    ChartLabel[] | undefined
  >(undefined);

  const [prevAssetClassWeightsHash, setPrevAssetClassWeightsHash] =
    useState<string>("");

  const { advisorId } = useSelector((state: State) => ({
    advisorId: state.main_Reducer.cockroach_advisor_id,
  }));

  const navigate = useNavigate();
  const location = useLocation();
  const notify = useStatusNotification();

  const { data: isAdmin } = useIsAdmin(advisorId);

  const urlQueryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const { state, dispatch } = usePortfolioBuilderContext();

  useEffect(() => {
    if (portfolioPlanId !== null) {
      return;
    }

    const modelNameOrNull = urlQueryParams.get("modelName");
    const taxTypeOrNull = urlQueryParams.get("taxType");
    const basePermissionOrNull = urlQueryParams.get("basePermission");

    const firm = urlQueryParams.get("firm") ?? undefined;
    const series = urlQueryParams.get("series") ?? undefined;
    const allocation = urlQueryParams.get("allocation") ?? undefined;

    if (
      modelNameOrNull === null ||
      taxTypeOrNull === null ||
      basePermissionOrNull === null
    ) {
      navigate(`/Advisor/${InvestmentsPath}/ModelMarketplaceV2`, {
        state: location.state,
      });
    }

    const modelName = modelNameOrNull as string;
    const taxType = taxTypeOrNull as PortfolioTaxType;
    const basePermission = basePermissionOrNull as BasePermission;

    urlQueryParams.delete("modelName");
    urlQueryParams.delete("firm");
    urlQueryParams.delete("series");
    urlQueryParams.delete("allocation");
    urlQueryParams.delete("taxType");
    urlQueryParams.delete("basePermission");

    navigate(
      {
        search: urlQueryParams.toString(),
      },
      { replace: true, state: location.state }
    );

    dispatch({
      type: SET_MODEL_DETAILS,
      payload: {
        modelName,
        firm,
        series,
        allocation,
        taxType,
        basePermission,
      },
    });

    dispatch({
      type: SET_TABLE,
      payload: DEFAULT_MODEL_TABLE, // a No asset class asset class with a single CASH security at weight 100%
    });

    // only want to run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNavBack = () => {
    if (portfolioPlanId === null) {
      navigate(`/Advisor/${InvestmentsPath}/ModelMarketplaceV2`, {
        state: location.state,
      });
    } else {
      navigate(
        `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/${portfolioPlanId}/ModelDetails`,
        { state: location.state }
      );
    }
  };

  const sideBarModelDetailsData = useMemo(() => {
    if (state.modelName === undefined || state.taxType === undefined) {
      return {
        name: "",
        tags: "",
        customModelDetails: undefined,
      };
    }

    if (
      state.firm === undefined ||
      state.series === undefined ||
      state.allocation === undefined
    ) {
      return {
        name: "",
        tags: "",
        customModelDetails: {
          modelName: state.modelName,
          taxType: state.taxType,
        },
      };
    }

    return {
      name: state.modelName,
      provider: state.firm,
      modelSeries: state.series,
      assetAllocation: state.allocation,
      tags: taxStatusIdToLabel[state.taxType],
      customModelDetails: undefined,
    };
  }, [state]);

  const auth = useAdvisorRequestAuth();
  const assetClassesAndTheirCategories = useGetAssetClasses(advisorId, auth);
  const existingPortfolioPlan = useGetPortfolioPlanById(
    portfolioPlanId,
    {},
    auth
  );
  const existingPortfolioAssetClasses = useGetPortfolioAssetClasses(
    portfolioPlanId,
    auth
  );
  const { mutateAsync: createPortfolios } = usePostPortfolioPlansBatch(auth);
  const classifyPortfolioPlan =
    useGetPositionsOrVirtualPortfolioPlanClassification(auth);
  const {
    fetchPortfolioStats: getStatsForPortfolioPlan,
    portfolioStats,
    hasFinMasonError,
    hasPortfolioStatsError,
    isLoadingPortfolioStats: isFetchingStats,
  } = useGetTransitionalProposalPlanStats(auth);
  const portfolioFamilyPath = useGetPortfolioPlanFamilyPath(
    portfolioPlanId,
    auth
  );

  const familyPath = useCreateFamilyPathFromData(portfolioFamilyPath.data); // Represents the Implementation Families structured in three levels: provider, series, and asset allocation.

  useEffect(() => {
    if (hasPortfolioStatsError) {
      if (hasFinMasonError instanceof FinMasonError) {
        notify(genFinMasonErrorMsg(hasFinMasonError), "Error");
      } else {
        notify("Error retrieving portfolio stats", "Error");
      }
    }
  }, [hasFinMasonError, hasPortfolioStatsError, notify]);

  useEffect(() => {
    if (
      portfolioPlanId !== null &&
      portfolioFamilyPath.isLoading === false &&
      portfolioFamilyPath.hasError === false &&
      existingPortfolioPlan.data !== undefined &&
      isMutating === false
    ) {
      dispatch({
        type: SET_MODEL_DETAILS,
        payload: {
          modelName: existingPortfolioPlan.data.data.plan.name,
          firm: familyPath?.provider,
          series: familyPath?.series,
          allocation: familyPath?.assetAllocation,
          taxType: getTaxTypeFromTags(
            existingPortfolioPlan.data.data.plan.tags
          ),
          basePermission: existingPortfolioPlan.data.data.plan.basePermission,
        },
      });
    }
  }, [
    isMutating,
    dispatch,
    portfolioPlanId,
    familyPath,
    portfolioFamilyPath.isLoading,
    portfolioFamilyPath.hasError,
    existingPortfolioPlan.data,
  ]);

  const securityIdsInExistingPortfolioPlan = useMemo(() => {
    if (existingPortfolioPlan.data !== undefined) {
      return existingPortfolioPlan.data.data.plan.entries
        .filter((el) => el.type === "Security")
        .map((el) => el.securityId);
    }

    return null;
  }, [existingPortfolioPlan.data]);

  const securitiesInPortfolioPlan = useSecuritiesBySecurityIds(
    securityIdsInExistingPortfolioPlan,
    auth
  );

  useEffect(() => {
    if (
      portfolioPlanId !== null &&
      existingPortfolioPlan.data !== undefined &&
      existingPortfolioAssetClasses.data !== undefined &&
      securitiesInPortfolioPlan.data !== undefined &&
      assetClassesAndTheirCategories.data !== undefined && //NOTE: we don't use this data/variable in here but it is a pre-req: when the modeTable that gets constructed in here is saved to the reducer/state it depends on having assetClassIds from this data source already saved in the reducer/state
      isMutating === false
    ) {
      const secWithMetaLookup: SecurityLookup =
        securitiesInPortfolioPlan.data.reduce((lookup, el) => {
          return {
            ...lookup,
            [el.id]: {
              ticker:
                (el.identifiers.find((i) => i.registrar === "TICKER")
                  ?.identifier as Ticker) ?? ("N/A" as Ticker),
              description: el.description ?? "N/A",
            },
          };
        }, {});

      let constructedModelTable: AssetClass[];

      if (existingPortfolioAssetClasses.data.data.length <= 0) {
        // no mappings so just stick all entries inside the "No asset class" asset class
        constructedModelTable =
          existingPortfolioPlan.data.data.plan.entries.reduce(
            (modelTable, entry) => {
              if (entry.type === "Security") {
                const securityWeight =
                  existingPortfolioPlan.data.data.plan.entries
                    .find(
                      (s) =>
                        s.type === "Security" &&
                        s.securityId === entry.securityId
                    )
                    ?.weight.mul(100)
                    .toString();

                return addSecurityToAssetClassInModel({
                  ticker:
                    secWithMetaLookup[entry.securityId]?.ticker ??
                    TEMPORARY_TICKER,
                  securityId: entry.securityId,
                  description:
                    secWithMetaLookup[entry.securityId]?.description ?? "",
                  assetClassElementId: NO_ASSET_CLASS_ASSET_CLASS.id,
                  weight: securityWeight,
                  modelTable: modelTable,
                });
              } else if (entry.type === "NestedPlan") {
                const sleeveWeight =
                  existingPortfolioPlan.data.data.plan.entries
                    .find(
                      (p) =>
                        p.type === "NestedPlan" && p.planId === entry.planId
                    )
                    ?.weight.mul(100)
                    .toString();

                return addSleeveToAssetClassInModel({
                  name: TEMPORARY_SLEEVE_NAME,
                  portfolioPlanId: entry.planId,
                  assetClassElementId: NO_ASSET_CLASS_ASSET_CLASS.id,
                  weight: sleeveWeight,
                  modelTable: modelTable,
                });
              } else {
                return modelTable; // don't care about "StrategicAllocation" entries right now
              }
            },
            [NO_ASSET_CLASS_ASSET_CLASS]
          );
      } else {
        constructedModelTable = existingPortfolioAssetClasses.data.data.reduce(
          (modelTable, ac) => {
            const assetClassElementId = generateElementId();

            const newModelTable = addAssetClassToModel({
              assetClassId: ac.assetClassId,
              assetClassName: ac.assetClassName,
              category: getAssetClassCategoryLabelFromEnum(
                ac.assetClassCategory
              ),
              elementId: assetClassElementId,
              modelTable: modelTable,
            });

            return ac.entries.reduce((mTable, entry) => {
              if (entry.entryType === "Security") {
                const securityWeight =
                  existingPortfolioPlan.data.data.plan.entries
                    .find(
                      (s) =>
                        s.type === "Security" &&
                        s.securityId === entry.entryTypeId
                    )
                    ?.weight.mul(100)
                    .toString();

                return addSecurityToAssetClassInModel({
                  ticker:
                    secWithMetaLookup[entry.entryTypeId]?.ticker ??
                    TEMPORARY_TICKER,
                  securityId: entry.entryTypeId,
                  description:
                    secWithMetaLookup[entry.entryTypeId]?.description ?? "",
                  assetClassElementId,
                  weight: securityWeight,
                  modelTable: mTable,
                });
              } else {
                const sleeveWeight =
                  existingPortfolioPlan.data.data.plan.entries
                    .find(
                      (p) =>
                        p.type === "NestedPlan" &&
                        p.planId === entry.entryTypeId
                    )
                    ?.weight.mul(100)
                    .toString();

                return addSleeveToAssetClassInModel({
                  name: TEMPORARY_SLEEVE_NAME,
                  portfolioPlanId: entry.entryTypeId,
                  assetClassElementId,
                  weight: sleeveWeight,
                  modelTable: mTable,
                });
              }
            }, newModelTable);
          },
          [NO_ASSET_CLASS_ASSET_CLASS]
        );
      }

      dispatch({
        type: SET_TABLE,
        payload: constructedModelTable,
      });
    }
  }, [
    isMutating,
    dispatch,
    portfolioPlanId,
    existingPortfolioPlan.data,
    existingPortfolioAssetClasses.data,
    securitiesInPortfolioPlan.data,
    assetClassesAndTheirCategories.data,
  ]);

  const onTrackEvent = () => {
    trackEvent({
      name: "Client Click Farther-logo",
    });
  };

  useEffect(() => {
    trackEvent({
      name: "Advisor Open Portfolio Lab",
    });
  }, []);

  const onClickPopoverMenuItem = () => {
    setShowPopoverMenu(false);
    setShowCustomModelModal(true);
  };

  const closeCustomModelModal = () => setShowCustomModelModal(false);

  const onEditCustomModel = (modelDetails: ModelDetails) => {
    dispatch({
      type: SET_MODEL_DETAILS,
      payload: {
        ...modelDetails,
      },
    });

    setShowCustomModelModal(false);
  };

  const [
    prevTotalWeightOfNonAssetClasses,
    setPrevTotalWeightOfNonAssetClasses,
  ] = useState<number>(0);

  // excludes weight of asset classes - sum of weights of all children of asset classes
  const totalWeightOfNonAssetClasses = useMemo(
    () => getTotalWeightOfNonAssetClasses(state.modelTable).toNumber(),
    [state.modelTable]
  );

  const onSavePortfolio = () => {
    if (totalWeightOfNonAssetClasses !== 100) {
      notify("Model not saved. Model must sum to 100.00%", "Error");
      return;
    }

    if (
      hasLooseSecuritiesOrSleeves(state.modelTable) &&
      state.modelTable.length >= 2
    ) {
      notify(
        "All securities and sleeves must be assigned to an asset class or no asset class. Cannot have a mix of both.",
        "Error"
      );
      return;
    }

    if (areAnyWeightsZero(state.modelTable)) {
      notify("Some weights are 0%", "Error");
      return;
    }

    if (
      state.modelTable
        .filter((ac) => ac.isInvisible === false)
        .some((ac) => ac.children.length === 0)
    ) {
      notify("Some asset classes are empty", "Error");
      return;
    }

    if (portfolioPlanId === null) {
      onCreatePortfolio();
    } else {
      onUpdatePortfolio();
    }
  };

  const onCreatePortfolio = async () => {
    if (
      state.modelName === undefined ||
      state.taxType === undefined ||
      advisorId === null
    ) {
      return;
    }

    if (
      state.firm !== undefined &&
      (state.series === undefined || state.allocation === undefined)
    ) {
      return;
    }

    const familyPath = constructFamilyPath({
      firm: state.firm,
      series: state.series,
      allocation: state.allocation,
    });

    const savedWithAssetClasses = state.modelTable.filter(
      (ac) => ac.isInvisible === false && ac.children.length > 0
    );

    const portfolioPlan = {
      name: state.modelName,
      tags: taxMapping[state.taxType],
      entries: getFirstLevelSecuritiesAndSleeves(state.modelTable),
      transformers: [],
      basePermission: state.basePermission,
      portfolioPlanId: generatePortfolioPlanId() as PortfolioPlanId,
      organization: "Farther" as const,
      ownerId: advisorId as unknown as PersonId,
      ...(familyPath && {
        portfolioFamilyPath: familyPath,
      }),
    };

    try {
      setIsMutating(true);
      // using the batch endpoint for now so that we can publish it right away
      const res = await createPortfolios({
        data: [portfolioPlan],
        dryRun: false,
      });

      if (res.success === true) {
        const portfolioId = res.data[0].portfolioPlanId;

        notify("Portfolio created", "Success");

        trackEvent({
          name: savedWithAssetClasses
            ? "Advisor Save Portfolio Lab Changes With Asset Classes"
            : "Advisor Save Portfolio Lab Changes Without Asset Classes",
        });

        navigate(
          `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/${portfolioId}/ModelDetails`,
          { state: location.state }
        );
      } else {
        notify("Error creating portfolio", "Error");
      }
    } catch (_e) {
      notify("Error creating portfolio", "Error");
    } finally {
      setIsMutating(false);
    }
  };

  const onUpdatePortfolio = async () => {
    if (existingPortfolioPlan.data === undefined) {
      return;
    }

    if (
      state.modelName === undefined ||
      state.taxType === undefined ||
      advisorId === null
    ) {
      return;
    }

    if (
      state.firm !== undefined &&
      (state.series === undefined || state.allocation === undefined)
    ) {
      return;
    }

    const portfolioId = existingPortfolioPlan.data.data.plan.portfolioPlanId;

    const familyPath = constructFamilyPath({
      firm: state.firm,
      series: state.series,
      allocation: state.allocation,
    });

    const savedWithAssetClasses = state.modelTable.filter(
      (ac) => ac.isInvisible === false && ac.children.length > 0
    );

    const portfolioPlan = {
      name: state.modelName,
      tags: taxMapping[state.taxType],
      entries: getFirstLevelSecuritiesAndSleeves(state.modelTable),
      transformers: existingPortfolioPlan.data.data.plan.transformers,
      basePermission: state.basePermission,
      portfolioPlanId: portfolioId,
      organization: existingPortfolioPlan.data.data.plan.organization,
      ownerId: advisorId as unknown as PersonId,
      ...(familyPath && {
        portfolioFamilyPath: familyPath,
      }),
    };

    try {
      setIsMutating(true);
      // using the batch endpoint for now so that we can publish it right away
      // this upserts/overwrites the existing portfolio
      const res = await createPortfolios({
        data: [portfolioPlan],
        dryRun: false,
      });

      if (res.success === true) {
        notify("Portfolio updated", "Success");

        trackEvent({
          name: savedWithAssetClasses
            ? "Advisor Save Portfolio Lab Changes With Asset Classes"
            : "Advisor Save Portfolio Lab Changes Without Asset Classes",
        });

        navigate(
          `/Advisor/${InvestmentsPath}/ModelMarketplaceV2/${portfolioId}/ModelDetails`,
          { state: location.state }
        );
      }
    } catch (_e) {
      notify("Error updating portfolio", "Error");
    } finally {
      setIsMutating(false);
    }
  };

  const [isFetchingClassification, setIsFetchingClassification] =
    useState<boolean>(false);
  const [hasErrorFetchingClassification, setHasErrorFetchingClassification] =
    useState<boolean>(false);

  useEffect(() => {
    const hasLooseSecuritiesOrSleevesAndAssetClasses =
      hasLooseSecuritiesOrSleeves(state.modelTable) &&
      state.modelTable.length >= 2;
    if (hasLooseSecuritiesOrSleevesAndAssetClasses) {
      return; // don't attempt to classify or get stats if there is a mix of loose securities and sleeves, and asset classes with or without securities and/or sleeves
    }

    const weightsHash = computeAssetClassWeightsHash(state.modelTable);

    const triggerGetClassificationAndStats =
      totalWeightOfNonAssetClasses === 100 &&
      (totalWeightOfNonAssetClasses !== prevTotalWeightOfNonAssetClasses ||
        weightsHash !== prevAssetClassWeightsHash);

    if (triggerGetClassificationAndStats) {
      if (!hasLooseSecuritiesOrSleeves(state.modelTable)) {
        // get asset class category allocations based on how user entered them in the portfolio builder
        const allocations = getAssetClassAllocations(state.modelTable);

        // transforms an AssetClassAllocationsObject to a ChartLabel[] that the Charts component knows how to display
        const chartLabels: ChartLabel[] = Object.entries(allocations)
          .map(([category, weight]) => ({
            label: AssetClassCategory.parse(
              category === "Cash Equivalents" ? "Cash" : category
            ),
            value: weight,
          }))
          .filter((cat) => cat.value > 0);

        setPortfolioClassification(chartLabels);
      } else {
        // hit FinMason api to get asset class category allocations for securities and (securities within) sleeves
        const planForClassification = {
          entries: getFirstLevelSecuritiesAndSleeves(state.modelTable, true),
          transformers: [],
        };

        const getClassification = async () => {
          try {
            setIsFetchingClassification(true);
            const res = await classifyPortfolioPlan({
              type: "virtual_portfolio_plan",
              virtualPlan: planForClassification,
            });
            if (res instanceof FinMasonError) {
              notify(genFinMasonErrorMsg(res), "Error");
              setHasErrorFetchingClassification(true);
            } else {
              setPortfolioClassification(getAllocations(res));
              setHasErrorFetchingClassification(false);
            }
          } catch (_e) {
            notify("Error classifying portfolio", "Error");
            setHasErrorFetchingClassification(true);
          } finally {
            setIsFetchingClassification(false);
          }
        };

        getClassification();
      }

      const planForStats = {
        entries: getFirstLevelSecuritiesAndSleeves(state.modelTable),
        transformers: [] as [],
      };

      getStatsForPortfolioPlan({
        type: "virtual_portfolio_plan",
        virtualPlan: planForStats,
      });
    }

    if (totalWeightOfNonAssetClasses !== prevTotalWeightOfNonAssetClasses) {
      setPrevTotalWeightOfNonAssetClasses(totalWeightOfNonAssetClasses);
    }

    if (weightsHash !== prevAssetClassWeightsHash) {
      setPrevAssetClassWeightsHash(weightsHash);
    }
  }, [
    state.modelTable,
    classifyPortfolioPlan,
    notify,
    totalWeightOfNonAssetClasses,
    prevTotalWeightOfNonAssetClasses,
    getStatsForPortfolioPlan,
    prevAssetClassWeightsHash,
  ]);

  return (
    <>
      {isMutating && <LogoLoadingStill onTop={true} />}

      {!isAdmin && showCustomModelModal && (
        <CreateCustomModelModal
          isOpen={showCustomModelModal}
          onClose={closeCustomModelModal}
          onSubmit={onEditCustomModel}
          currentModelName={state.modelName}
          currentTaxType={state.taxType}
        />
      )}

      {isAdmin && showCustomModelModal && (
        <CreateCustomOrPartnerModelModal
          isOpen={showCustomModelModal}
          onClose={closeCustomModelModal}
          onSubmit={onEditCustomModel}
          currentModelName={state.modelName}
          currentFirm={state.firm}
          currentSeries={state.series}
          currentAllocation={state.allocation}
          currentTaxType={state.taxType}
          currentBasePermission={state.basePermission}
        />
      )}

      <div className={styles.container}>
        <PageHero
          className={styles.topBar}
          start={
            <div className={styles.flexRow}>
              <div className={styles.farthermark}>
                <NavLink to={`/Advisor/Clients`} onClick={onTrackEvent}>
                  <LogoWordmark className={styles.fartherLogo} />
                </NavLink>
              </div>

              <Text variant="body" color={"textSubtle"}>
                Create a Model Portfolio
              </Text>

              <ChevronRightIcon className={styles.chevronRightIconDivider} />

              <Text variant="body" color={"text"}>
                Custom Model
              </Text>
            </div>
          }
        />

        <PageHero
          className={styles.hero}
          start={
            <div className={styles.flexRow}>
              <ArrowBackIcon className={styles.arrowIcon} onClick={onNavBack} />

              <Text className={styles.marginRight} variant="title1">
                Allocate your portfolio
              </Text>

              <ThreeDotsPopoverMenu
                anchorRef={anchorRef}
                isOpen={showPopoverMenu}
                onOpen={() => setShowPopoverMenu(true)}
                onClose={() => setShowPopoverMenu(false)}
                anchorOriginHorizontal={"left"}
                transformOriginHorizontal={"left"}
              >
                <MenuItem onClick={onClickPopoverMenuItem}>Settings</MenuItem>
              </ThreeDotsPopoverMenu>
            </div>
          }
          end={
            <div className={styles.flexRow}>
              <LinkButton
                text="Cancel"
                variant="text"
                buttonType="primary"
                to={"/Advisor/Investments/ModelMarketplaceV2"}
              />

              <div className={styles.continueButton}>
                <Button
                  text={"Save Model"}
                  variant={"outlined"}
                  buttonType={"secondary"}
                  disabled={false}
                  sx={{ width: "200px" }}
                  onClick={onSavePortfolio}
                />
              </div>
            </div>
          }
        />

        <div className={styles.body}>
          <div className={styles.contentLeft}>
            <SearchBarAndTableConstructor portfolioPlanId={portfolioPlanId} />

            <div className={styles.contentLeftScroll}>
              <ModelBuilderTable />
            </div>
          </div>

          <div className={styles.contentRight}>
            <Details
              data={sideBarModelDetailsData}
              isLoading={false}
              hasError={false}
            />

            <Charts
              classification={portfolioClassification}
              isLoadingIncomingClassification={isFetchingClassification}
              hasErrorIncomingClassification={hasErrorFetchingClassification}
            />

            <Stats
              isLoading={isFetchingStats}
              hasError={hasPortfolioStatsError}
              data={portfolioStats}
              hideMetricVisibilityTogglesAndShowAllStats={true}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PortfolioBuilder;
